/**
 * Turn a raw user prompt into a short, friendly human summary.
 *
 * The generation screen leads with this string in place of the truncated
 * raw prompt, so it has to read like a description, not a fragment. The
 * shaping rules in order:
 *
 *   1. Trim. Empty input falls back to "Your app".
 *   2. Take the first sentence — split on . ! ? — or the first 120 chars
 *      if no terminator appears in that window.
 *   3. If the result is still > 100 chars, truncate at the last comma or
 *      whitespace before 100 so we cut on a natural boundary, not mid-word.
 *   4. Strip trailing punctuation (a sentence-shaped summary doesn't need
 *      to end with a period).
 *   5. Capitalize the first character.
 *
 * Example
 *   "A pro-grade crypto and stock portfolio tracker. Dashboard with
 *    total portfolio value, day's change, all-time gain…"
 *   → "A pro-grade crypto and stock portfolio tracker"
 */
/**
 * Strip leading markdown noise from a string: hashes, list bullets, blockquote
 * carets, and the surrounding whitespace. The model occasionally emits a
 * heading like "## 2" or a bullet "- App name" inside what should be a plain
 * line. Trimming these defensively keeps the rendered heading clean even when
 * the upstream contract slips. Idempotent.
 */
export function stripMarkdownLead(s: string): string {
  return s.replace(/^[#*\->\s]+/u, '');
}

export function summarizePrompt(prompt: string): string {
  // Strip any leading markdown noise BEFORE the rest of the logic — the
  // model has been observed emitting "## 2" as a stand-in for an app name,
  // and the trimmed input is what the rest of the shaping operates on.
  const trimmed = stripMarkdownLead(prompt.trim());
  if (!trimmed) return 'Your app';

  // (2) First sentence — or the first 120 chars if no terminator is found.
  let head = trimmed;
  const window120 = trimmed.slice(0, 120);
  const match = window120.match(/[.!?]/);
  if (match && match.index !== undefined) {
    head = window120.slice(0, match.index);
  } else if (trimmed.length > 120) {
    head = window120;
  }

  // (3) Soft 100-char cap, snapping back to the last natural break.
  if (head.length > 100) {
    const cap = head.slice(0, 100);
    const lastComma = cap.lastIndexOf(',');
    const lastSpace = cap.lastIndexOf(' ');
    const breakAt = Math.max(lastComma, lastSpace);
    head = breakAt > 40 ? cap.slice(0, breakAt) : cap;
  }

  // (4) Drop trailing punctuation + whitespace — including the comma we may
  // have just snapped to.
  head = head.replace(/[\s.,!?:;\-–—]+$/u, '');

  if (!head) return 'Your app';

  // (5) Title-case the very first character only. Use codePointAt so we
  // handle surrogate-pair starts (rare for prompts, cheap to be correct).
  const first = head.charAt(0);
  const upper = first.toUpperCase();
  if (first === upper) return head;
  return upper + head.slice(1);
}
